import { injectable } from 'inversify';
import { computed } from 'mobx';
import {
    CreditCardInfo,
    Invoice,
    InvoicesApiService,
    ChargeApiService,
    ChargeCustomerACHRequest,
    BankAccountInfo,
    ChargeType,
    ChargeCustomerStoredPaymentMethodRequest,
} from 'src/api';
import { AnalyticsStore } from 'src/core/stores/analytics.store';
import { ClientDataStore } from 'src/core/stores/client-data.store';
import { HomeStore } from 'src/core/stores/home.store';
import { RouterStore } from 'src/core/stores/router.store';
import { SavedCreditCardsStore } from 'src/payment-method/stores/saved-credit-cards.store';

export interface PendingPayment {
    invoiceNumber: string;
    amount?: number;
    card?: Partial<CreditCardInfo>;
    bankAccount?: Partial<BankAccountInfo>;
    needToSaveCard?: boolean;
}

export interface PaymentConfirmation {
    number: string;
    amountPaid: number;
    remainingBalance: number;
    notificationEmail: string;
    transaction: string;
    invoiceNumber: string;
    customerId: number;
}

@injectable()
export class InvoiceStore {
    payment: PendingPayment | undefined;

    private invoiceCache: { [id: string]: Invoice } = {};
    private paymentConfirmationCache: { [id: string]: PaymentConfirmation } = {};

    constructor(
        private invoicesApi: InvoicesApiService,
        private chargeApi: ChargeApiService,
        private analytics: AnalyticsStore,
        private clientData: ClientDataStore,
        private router: RouterStore,
        private readonly home: HomeStore,
        private savedCreditCardsStore: SavedCreditCardsStore
    ) { }

    getInvoice(id: string, forceRefresh?: boolean): Promise<Invoice> {
        const trimmedId = id.trim();

        if (!forceRefresh) {
            const cachedInvoice = this.invoiceCache[trimmedId];
            if (cachedInvoice) {
                return Promise.resolve(cachedInvoice);
            }
        }

        return this.invoicesApi.getInvoiceById(trimmedId, true).then(
            (resp) => {
                if (
                    resp.data.creditCardDisabled &&
                    resp.data.achDisabled &&
                    resp.data.payByBankDisabled &&
                    this.clientData.isAnonymousPayments
                ) {
                    this.router.history.replace('/invoice/expired-link');
                    return new Promise<never>(() => { });
                }

                this.invoiceCache[trimmedId] = resp.data;
                return Promise.resolve(resp.data);
            },
            (err) => {
                if (this.clientData.isAnonymousPayments) {
                    this.router.history.replace('/invoice/expired-link');
                    return new Promise<never>(() => { });
                }

                throw err;
            }
        );
    }

    submitAchPayment(
        payment: PendingPayment,
        isBusinessPayment: boolean
    ): Promise<PaymentConfirmation> {
        if (!payment || !payment.amount || !payment.bankAccount) {
            return Promise.reject('Invalid input');
        }

        const invoice = this.invoiceCache[payment.invoiceNumber];
        if (!invoice) {
            return Promise.reject('Missing invoice');
        }
        const amount = payment.amount;
        const bankAccount: BankAccountInfo = {
            name: payment.bankAccount!.name!,
            routing: payment.bankAccount!.routing!,
            number: payment.bankAccount!.number!,
            checkNumber: '',
        };

        const request: ChargeCustomerACHRequest = {
            isBusinessPayment,
            amount,
            invoice: payment.invoiceNumber,
            chargeType: ChargeType.NUMBER_0,
            method: bankAccount,
            notificationEmail: this.clientData.loginData.email,
            splits: [
                {
                    invoiceId: payment.invoiceNumber,
                    amountApplied: payment.amount,
                    usesPaymentsService: false,
                },
            ],
            storePaymentMethod: false,
        };
        return this.chargeApi
            .chargeBankAccount(request, true, {
                400: 'Failed to charge bank account. Verify the information you’ve entered or try a different bank account.',
            })
            .then(
                (resp) => {
                    // payment has been submitted, do not reuse
                    this.payment = undefined;
                    // invalidate cached invoice because there's a new payment
                    delete this.invoiceCache[payment.invoiceNumber];

                    const { data } = resp;
                    const confirmation: PaymentConfirmation = {
                        number: data.payment,
                        amountPaid: amount,
                        remainingBalance: invoice.balance - amount,
                        notificationEmail: data.notificationEmail,
                        transaction: data.transaction,
                        invoiceNumber: invoice.number || invoice.id.toString(),
                        customerId: invoice.customerId,
                    };

                    this.paymentConfirmationCache[confirmation.number] = confirmation;
                    this.analytics.paymentAttemptSuccess(
                        invoice.number || invoice.id.toString(),
                        invoice.customerId,
                        amount
                    );

                    return confirmation;
                },
                (err) => {
                    this.analytics.paymentAttemptFail(
                        invoice.number || invoice.id.toString(),
                        invoice.customerId,
                        amount,
                        err.message
                    );
                    throw err;
                }
            );
    }

    submitPaymentToUseStoredPaymentMethod(
        payment: PendingPayment | undefined
    ): Promise<PaymentConfirmation> {
        if (!payment || !payment.amount || !(payment.card || payment.bankAccount)) {
            return Promise.reject('Invalid input');
        }
        const amount = payment.amount;
        const invoice = this.invoiceCache[payment.invoiceNumber];
        if (!invoice) {
            return Promise.reject('Missing invoice');
        }

        if (!this.savedCreditCardsStore.selectedCardToPay) {
            return Promise.reject('Need to selected a stored payment method');
        }

        const request: ChargeCustomerStoredPaymentMethodRequest = {
            amount,
            method: { id: this.savedCreditCardsStore.selectedCardToPay.id },
            notificationEmail: this.clientData.loginData.email,
            chargeType: ChargeType.NUMBER_0,
            splits: [
                {
                    invoiceId: payment.invoiceNumber,
                    amountApplied: payment.amount,
                    usesPaymentsService: false, // this doesn't matter because it's a stored payment method so no need for gateway
                },
            ],
            invoice: payment.invoiceNumber,
            storePaymentMethod: false,
            isCreditCard: true
        };

        return this.chargeApi
            .chargeStoredPaymentMethod(request, true, {
                400: 'Failed to charge card. Verify the stored card you’ve selected or try a different card.',
            })
            .then(
                (resp) => {
                    // payment has been submitted, do not reuse
                    this.payment = undefined;
                    // invalidate cached invoice because there's a new payment
                    delete this.invoiceCache[payment.invoiceNumber];

                    const { data } = resp;
                    const confirmation: PaymentConfirmation = {
                        number: data.payment,
                        amountPaid: amount,
                        remainingBalance: invoice.balance - amount,
                        notificationEmail: data.notificationEmail,
                        transaction: data.transaction,
                        invoiceNumber: invoice.number || invoice.id.toString(),
                        customerId: invoice.customerId,
                    };

                    this.paymentConfirmationCache[confirmation.number] = confirmation;
                    this.analytics.paymentAttemptSuccess(
                        invoice.number || invoice.id.toString(),
                        invoice.customerId,
                        amount
                    );

                    return confirmation;
                },
                (err) => {
                    this.analytics.paymentAttemptFail(
                        invoice.number || invoice.id.toString(),
                        invoice.customerId,
                        amount,
                        err.message
                    );
                    throw err;
                }
            );
    }

    submitPayment(payment: PendingPayment): Promise<PaymentConfirmation> {
        if (!payment || !payment.amount || !(payment.card || payment.bankAccount)) {
            return Promise.reject('Invalid input');
        }

        const invoice = this.invoiceCache[payment.invoiceNumber];
        if (!invoice) {
            return Promise.reject('Missing invoice');
        }

        const amount = payment.amount;

        return this.chargeApi
            .chargeCreditCard(
                {
                    amount,
                    storePaymentMethod: payment.needToSaveCard || false,
                    invoice: payment.invoiceNumber,
                    method: payment.card as CreditCardInfo,
                    notificationEmail: this.clientData.loginData.email,
                },
                true,
                {
                    400: 'Failed to charge card. Verify the information you’ve entered or try a different card.',
                }
            )
            .then(
                (resp) => {
                    // payment has been submitted, do not reuse
                    this.payment = undefined;
                    // invalidate cached invoice because there's a new payment
                    delete this.invoiceCache[payment.invoiceNumber];

                    const { data } = resp;
                    const confirmation: PaymentConfirmation = {
                        number: data.payment,
                        amountPaid: amount,
                        remainingBalance: invoice.balance - amount,
                        notificationEmail: data.notificationEmail,
                        transaction: data.transaction,
                        invoiceNumber: invoice.number || invoice.id.toString(),
                        customerId: invoice.customerId,
                    };

                    this.paymentConfirmationCache[confirmation.number] = confirmation;
                    this.analytics.paymentAttemptSuccess(
                        invoice.number || invoice.id.toString(),
                        invoice.customerId,
                        amount
                    );

                    return confirmation;
                },
                (err) => {
                    this.analytics.paymentAttemptFail(
                        invoice.number || invoice.id.toString(),
                        invoice.customerId,
                        amount,
                        err.message
                    );
                    throw err;
                }
            );
    }

    getCachedConfirmation(confirmationNumber: string): PaymentConfirmation | undefined {
        return this.paymentConfirmationCache[confirmationNumber];
    }

    getCachedInvoice(id: string): Invoice | undefined {
        return this.invoiceCache[id];
    }

    setCachedConfirmation(confirmationNumber: string, paymentConfirmation: PaymentConfirmation) {
        this.paymentConfirmationCache[confirmationNumber] = paymentConfirmation;
    }

    getOpenInvoices(): Promise<Invoice[]> {
        return this.invoicesApi.getInvoices(true).then((resp) => {
            const invoices = resp.data;
            invoices.forEach((invoice) => {
                return (this.invoiceCache[invoice.id] = invoice);
            });
            return invoices;
        });
    }

    getSavedCreditCardStore = () => this.savedCreditCardsStore;

    @computed get showLoginLink() {
        return !this.clientData.isLoggedIn && this.savedCreditCardsStore.isSavedCreditCardFeatureOn;
    }

    @computed get needToChargeStoredCard() {
        return this.showStoreOption && this.savedCreditCardsStore.selectedCardToPay;
    }

    @computed get showStoreOption() {
        return this.clientData.isLoggedIn && this.savedCreditCardsStore.isSavedCreditCardFeatureOn;
    }
}
